// src/components/empleado/SolicitarAusenciaForm.jsx
import React from "react";
import { TIPOS, SUBTIPOS, TIPO_THEME, PARCIAL_SUGERENCIAS } from "../../utils/ausenciasCatalogo";
import { ausenciasService } from "../../services/ausencias";

const fmtH = (t) => (t ? String(t).slice(0, 5) : "");
const fmtFechaES = (iso) => {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return iso;
};

/* ================== Detección de subtipos POR HORAS ================== */
const SUBTIPOS_POR_HORAS_IDS = new Set([
  "ACOMPANAMIENTO_MEDICO",
  "CITA_MEDICA",
  "ASUNTOS_PROPIOS",
  "CUIDADO_DEL_LACTANTE",
  "DEBER_INEXCUSABLE",
  "DEBER_INEXCUSABLE_PUBLICO_PERSONAL",
  "EXAMEN_OFICIAL",
]);
const SUBTIPOS_POR_HORAS_LABELS = new Set([
  "Acompañamiento médico",
  "Cita médica",
  "Asuntos propios",
  "Cuidado del lactante",
  "Deber inexclusable público/personal",
  "Deber inexcusable público/personal",
  "Examen oficial",
]);
const toKey = (s) => (s || "").toString().trim().toUpperCase().replace(/\s+/g, "_");
const normH = (s) => (s ? (s.length === 5 ? s + ":00" : s) : null);
function esSubtipoPorHoras(subtipo) {
  if (!subtipo) return false;
  const id = toKey(subtipo.modelSubtipo || "");
  if (SUBTIPOS_POR_HORAS_IDS.has(id)) return true;
  const lbl = (subtipo.label || "").trim();
  return SUBTIPOS_POR_HORAS_LABELS.has(lbl);
}

/* ================== Helper: ¿Se permite medio día? ================== */
function permiteMedioDia(tipo, subtipo, reglaTipo) {
  // 1) Permiso explícito desde el subtipo (catálogo)
  const overrideSubtipo = !!(subtipo?.defaults?.permite_mediodia ?? subtipo?.permite_mediodia);
  // 2) Caso especial: "Permisos → Otro" admite ambos (día completo y parcial)
  const esOtro =
    (subtipo && typeof subtipo.modelSubtipo === "string" && subtipo.modelSubtipo.toUpperCase() === "OTRO") ||
    (subtipo && typeof subtipo.label === "string" && subtipo.label.trim().toUpperCase() === "OTRO");
  // 3) Permiso general por tipo según reglas, excepto VACACIONES
  const tipoPermite = !!reglaTipo?.permite_mediodia && tipo?.modelTipo !== "VACACIONES";
  return overrideSubtipo || esOtro || tipoPermite;
}

/* ================== Utilidad local de cómputo ================== */
function diasLaborablesFallback(desde, hasta) {
  if (!desde || !hasta) return 0;
  const a = new Date(`${desde}T00:00:00`);
  const b = new Date(`${hasta}T00:00:00`);
  if (b < a) return 0;
  let c = 0;
  for (let d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) {
    const wd = d.getDay(); // 0=dom,6=sáb
    if (wd !== 0 && wd !== 6) c += 1;
  }
  return c;
}

export default function SolicitarAusenciaForm({ token, emailUsuario }) {
  // Selección
  const [tipo, setTipo] = React.useState(TIPOS[1]); // por defecto Permisos
  const [subtipo, setSubtipo] = React.useState(null);

  // Rango y horas
  const [fechaInicio, setFechaInicio] = React.useState("");
  const [fechaFin, setFechaFin] = React.useState("");
  const [horaInicio, setHoraInicio] = React.useState("");
  const [horaFin, setHoraFin] = React.useState("");

  // Flags y campos
  const [parcial, setParcial] = React.useState(false);
  const [retribuida, setRetribuida] = React.useState(true);
  const [motivo, setMotivo] = React.useState("");

  // Estado de red/validación
  const [enviando, setEnviando] = React.useState(false);
  const [msg, setMsg] = React.useState("");
  const [rules, setRules] = React.useState([]);
  const [reglaTipo, setReglaTipo] = React.useState(null);
  const [check, setCheck] = React.useState(null);
  const [checking, setChecking] = React.useState(false);
  const [valError, setValError] = React.useState("");

  // Cargar reglas una vez
  React.useEffect(() => {
    let mounted = true;
    ausenciasService
      .reglas(token)
      .then((r) => mounted && setRules(r?.rules || []))
      .catch(() => mounted && setRules([]));
    return () => {
      mounted = false;
    };
  }, [token]);

  // Cambio de tipo -> defaults
  React.useEffect(() => {
    const subs = SUBTIPOS[tipo.modelTipo] || [];
    setSubtipo(subs[0] || null);
    setParcial(Boolean(tipo.fixed?.parcial));
    setRetribuida(tipo.fixed?.retribuida ?? true);
    if (tipo.modelTipo === "VACACIONES") {
      setHoraInicio("");
      setHoraFin("");
    }
  }, [tipo]);

  // Cambio de subtipo -> forzar parcial/horas si es de horas
  React.useEffect(() => {
    if (!subtipo) return;

    const esHoras = esSubtipoPorHoras(subtipo);

    // Defaults del subtipo si existieran en tu catálogo
    const defs = subtipo.defaults || {};
    if (typeof defs.retribuida === "boolean") setRetribuida(defs.retribuida);

    if (esHoras) {
      setParcial(true);
      if (!horaInicio) setHoraInicio("09:00");
      if (!horaFin) {
        const base = "09:00";
        const [h, m] = (horaInicio || base).split(":").map(Number);
        const durMin =
          (PARCIAL_SUGERENCIAS[subtipo.modelSubtipo]?.horas || 2) * 60 +
          (PARCIAL_SUGERENCIAS[subtipo.modelSubtipo]?.minutos || 0);
        const hi = h * 60 + m, hf = hi + durMin;
        const H = String(Math.floor(hf / 60)).padStart(2, "0");
        const M = String(hf % 60).padStart(2, "0");
        setHoraFin(`${H}:${M}`);
      }
    } else {
      if (parcial && !permiteMedioDia(tipo, subtipo, reglaTipo)) setParcial(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtipo]);

  // Recalcular regla de tipo al cambiar rules/tipo
  React.useEffect(() => {
    const r = (rules || []).find((x) => x.tipo === tipo.modelTipo) || null;
    setReglaTipo(r);
  }, [rules, tipo]);

  // ===== validación (debounce) =====
  const debounceRef = React.useRef(null);
  React.useEffect(() => {
    setValError("");
    setCheck(null);

    const esHoras = esSubtipoPorHoras(subtipo);
    const esMultiDia = !!fechaInicio && !!fechaFin && fechaInicio !== fechaFin;

    if (
      parcial &&
      !esHoras &&
      (tipo.modelTipo === "VACACIONES" ||
        !permiteMedioDia(tipo, subtipo, reglaTipo) ||
        esMultiDia)
    ) {
      setParcial(false);
      return;
    }

    if (!fechaInicio || !fechaFin) return;

    const esParcialConHorasMismoDia =
      (parcial || esHoras) &&
      !esMultiDia &&
      !!horaInicio &&
      !!horaFin &&
      horaFin > horaInicio;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setChecking(true);
      try {
        const payload = {
          usuario_email: emailUsuario,
          tipo: tipo.modelTipo,
          subtipo: subtipo?.modelSubtipo,
          desde: fechaInicio,
          hasta: fechaFin,
          medio_dia: !!(parcial || esHoras),
          ...(esParcialConHorasMismoDia && {
            parcial: true,
            hora_inicio: `${horaInicio}:00`,
            hora_fin: `${horaFin}:00`,
          }),
        };
        const res = await ausenciasService.validar(token, payload);
        setCheck(res);
        setValError(res.allowed ? "" : res.reason || "Solicitud no permitida");
      } catch (e) {
        setCheck(null);
        setValError(e?.message || "No se pudo validar (modo estimado).");
      } finally {
        setChecking(false);
      }
    }, 300);

    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [
    token,
    emailUsuario,
    tipo.modelTipo,
    fechaInicio,
    fechaFin,
    parcial,
    subtipo,
    reglaTipo,
    horaInicio,
    horaFin,
  ]);

  // ===== derivados =====
  const horasParciales = React.useMemo(() => {
    const esHoras = esSubtipoPorHoras(subtipo);
    if (!(parcial || esHoras) || !horaInicio || !horaFin) return 0;
    const [h1, m1] = horaInicio.split(":").map(Number);
    const [h2, m2] = horaFin.split(":").map(Number);
    const min = h2 * 60 + m2 - (h1 * 60 + m1);
    return Math.max(0, min);
  }, [parcial, subtipo, horaInicio, horaFin]);

  const requestedDays = React.useMemo(() => {
    if (check?.requested_days != null) return check.requested_days;
    if (!parcial) return diasLaborablesFallback(fechaInicio, fechaFin);
    return 0.5;
  }, [check, parcial, fechaInicio, fechaFin]);

  const esHoras = esSubtipoPorHoras(subtipo);
  const puedeEnviar =
    !!emailUsuario &&
    !!subtipo &&
    !!fechaInicio &&
    !!fechaFin &&
    !checking &&
    (check ? !!check.allowed : true) &&
    (!(parcial || esHoras) || (horaInicio && horaFin && horaFin > horaInicio));

  function validateFields() {
    if (!emailUsuario) return "Usuario no válido.";
    if (!subtipo) return "Selecciona un motivo.";
    if (!fechaInicio || !fechaFin) return "Selecciona el rango de fechas.";
    if (new Date(`${fechaFin}T00:00:00`) < new Date(`${fechaInicio}T00:00:00`))
      return "La fecha fin no puede ser anterior a la de inicio.";
    if (tipo.modelTipo === "VACACIONES" && (parcial || esHoras))
      return "Las vacaciones no pueden ser parciales.";
    if (parcial || esHoras) {
      if (fechaInicio !== fechaFin)
        return "Las ausencias por horas deben ser en un único día.";
      if (!horaInicio || !horaFin) return "Indica hora inicio y hora fin.";
      if (horaFin <= horaInicio)
        return "La hora fin debe ser mayor que la hora inicio.";
      if (!permiteMedioDia(tipo, subtipo, reglaTipo) && !esHoras)
        return "El convenio no permite medio día para este tipo.";
    }
    return null;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setMsg("");
    const error = validateFields();
    if (error) {
      setMsg("❌ " + error);
      return;
    }

    // Re-evaluación defensiva de “por horas”
    const _esHoras = esSubtipoPorHoras(subtipo);
    if (_esHoras) {
      if (!horaInicio || !horaFin) {
        setMsg("❌ Faltan horas en una ausencia por horas.");
        return;
      }
    }

    try {
      setChecking(true);
      const esParcialConHorasMismoDia =
        (parcial || _esHoras) &&
        fechaInicio === fechaFin &&
        !!horaInicio &&
        !!horaFin &&
        horaFin > horaInicio;

      const payloadValidar = {
        usuario_email: emailUsuario,
        tipo: tipo.modelTipo,
        subtipo: subtipo?.modelSubtipo,
        desde: fechaInicio,
        hasta: _esHoras ? fechaInicio : fechaFin, // forzar mismo día si es por horas
        medio_dia: !!(parcial || _esHoras),
        ...(esParcialConHorasMismoDia && {
          parcial: true,
          hora_inicio: `${horaInicio}:00`,
          hora_fin: `${horaFin}:00`,
        }),
      };

      const res = await ausenciasService.validar(token, payloadValidar);
      setCheck(res);
      if (!res.allowed) {
        setMsg(
          "❌ " +
            (res.reason === "saldo_insuficiente"
              ? `No hay saldo suficiente. Pides ${res.requested_days}, disponibles ${res.available_days}.`
              : res.reason === "medio_dia_no_permitido"
              ? "El convenio no permite medio día para este tipo."
              : "Solicitud no permitida.")
        );
        return;
      }
    } catch {
      setMsg("⚠ No se pudo validar con el servidor; enviando igualmente…");
    } finally {
      setChecking(false);
    }

    const payload = {
      usuario_email: emailUsuario,
      tipo: tipo.modelTipo,
      subtipo: subtipo.modelSubtipo,
      fecha_inicio: fechaInicio,
      fecha_fin: (parcial || esHoras) ? fechaInicio : fechaFin,
      parcial: !!(parcial || esHoras),
      retribuida: !!retribuida,
      hora_inicio: (parcial || esHoras) ? normH(horaInicio) : null,
      hora_fin: (parcial || esHoras) ? normH(horaFin) : null,
      motivo: (motivo || "").trim(),
    };

    setEnviando(true);
    try {
      const created = await ausenciasService.crear(token, payload);
      setMsg(`✅ Solicitud enviada correctamente (#${created.id})`);
      setHoraInicio("");
      setHoraFin("");
      setMotivo("");
      setFechaInicio("");
      setFechaFin("");
      setParcial(Boolean(tipo.fixed?.parcial));
      setRetribuida(tipo.fixed?.retribuida ?? true);
      setCheck(null);
    } catch (err) {
      setMsg("❌ " + (err?.message || "Error al crear la solicitud"));
    } finally {
      setEnviando(false);
    }
  }

  const subopts = SUBTIPOS[tipo.modelTipo] || [];
  const permiteMedio = permiteMedioDia(tipo, subtipo, reglaTipo);
  const disableParcialByRange = !!fechaInicio && !!fechaFin && fechaInicio !== fechaFin;
  const disabledParcial = (
    esSubtipoPorHoras(subtipo) ||
    tipo.modelTipo === "VACACIONES" ||
    disableParcialByRange ||
    !permiteMedio
  );

  const fechaInicioMax = fechaFin || undefined;
  const fechaFinMin = fechaInicio || undefined;

  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
      {/* izquierda */}
      <div className="rounded-2xl bg-white/55 backdrop-blur-xl border border-white/50 shadow p-4 sm:p-5 space-y-4">
        <div className="flex items-center gap-2">
          <span className={`h-2.5 w-2.5 rounded-full ${TIPO_THEME[tipo.modelTipo]?.dot || "bg-slate-400"}`} />
          <h3 className="text-base sm:text-lg font-semibold text-slate-900">Solicitar Ausencia o Vacaciones</h3>
        </div>

        {/* Tipo / Subtipo */}
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Tipo de solicitud</label>
            <select
              className="mt-1 w-full px-3 py-2 rounded-lg bg-white/70 border border-white/60 shadow-sm"
              value={tipo.modelTipo}
              onChange={(e) => {
                const t = TIPOS.find((x) => x.modelTipo === e.target.value);
                setTipo(t || TIPOS[0]);
              }}
            >
              {TIPOS.map((t) => (
                <option key={t.modelTipo} value={t.modelTipo}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">Motivo / Subtipo</label>
            <select
              className="mt-1 w-full px-3 py-2 rounded-lg bg-white/70 border border-white/60 shadow-sm"
              value={subtipo?.modelSubtipo || ""}
              onChange={(e) => setSubtipo(subopts.find((s) => s.modelSubtipo === e.target.value) || null)}
            >
              {subopts.map((s) => (
                <option key={s.modelSubtipo} value={s.modelSubtipo}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Fechas */}
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Fecha inicio</label>
            <input
              type="date"
              className="mt-1 w-full px-3 py-2 rounded-lg bg-white/70 border border-white/60 shadow-sm"
              value={fechaInicio}
              onChange={(e) => {
                setFechaInicio(e.target.value);
                if (esSubtipoPorHoras(subtipo)) setFechaFin(e.target.value);
              }}
              max={fechaInicioMax}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Fecha fin</label>
            <input
              type="date"
              className="mt-1 w-full px-3 py-2 rounded-lg bg-white/70 border border-white/60 shadow-sm"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              min={fechaFinMin}
              disabled={esSubtipoPorHoras(subtipo)}
            />
          </div>
        </div>

        {/* Parcial / Retribuida */}
        <fieldset className="flex flex-wrap items-center gap-5" aria-describedby="ayuda-parcial">
          <legend className="sr-only">Opciones</legend>

          <label
            className="inline-flex items-center gap-2"
            title={
              esSubtipoPorHoras(subtipo)
                ? "Este motivo se solicita por horas"
                : !permiteMedio
                ? "El convenio o el subtipo no permiten medio día"
                : disableParcialByRange
                ? "Parciales solo en un único día"
                : ""
            }
          >
            <input
              type="checkbox"
              className="accent-indigo-600"
              checked={parcial || esSubtipoPorHoras(subtipo)}
              onChange={(e) => setParcial(e.target.checked)}
              disabled={disabledParcial}
            />
            <span className="text-sm">Parcial</span>
          </label>

          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                className="accent-indigo-600"
                checked={retribuida}
                onChange={(e) => setRetribuida(e.target.checked)}
                disabled={tipo.modelTipo === "VACACIONES"}
              />
              <span className="text-sm">Retribuida</span>
            </label>
            <span id="ayuda-parcial" className="text-xs text-slate-500">
              (si la política lo permite)
            </span>
          </div>
        </fieldset>

        {/* Horas si parcial o subtipo por horas */}
        {(parcial || esSubtipoPorHoras(subtipo)) && (
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Hora inicio</label>
              <input
                type="time"
                className="mt-1 w-full px-3 py-2 rounded-lg bg-white/70 border border-white/60 shadow-sm"
                value={horaInicio}
                onChange={(e) => setHoraInicio(e.target.value)}
                step="300"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Hora fin</label>
              <input
                type="time"
                className="mt-1 w-full px-3 py-2 rounded-lg bg-white/70 border border-white/60 shadow-sm"
                value={horaFin}
                onChange={(e) => setHoraFin(e.target.value)}
                step="300"
                min={horaInicio || undefined}
                aria-invalid={(horaInicio && horaFin && horaFin <= horaInicio) || undefined}
              />
            </div>
          </div>
        )}

        {/* Motivo */}
        <div>
          <label className="text-xs font-medium text-slate-600">Motivo (opcional)</label>
          <textarea
            rows={3}
            className="mt-1 w-full px-3 py-2 rounded-lg bg-white/70 border border-white/60 shadow-sm"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            maxLength={600}
            placeholder="Añade detalles si es necesario…"
          />
          <div className="text-[11px] text-slate-500 mt-1">{motivo.length}/600</div>
        </div>

        {/* Acciones */}
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button
            type="submit"
            disabled={enviando || !puedeEnviar}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 shadow"
          >
            {enviando ? "Enviando…" : "Solicitar"}
          </button>
          <button
            type="button"
            onClick={() => {
              setFechaInicio("");
              setFechaFin("");
              setHoraInicio("");
              setHoraFin("");
              setMotivo("");
              setParcial(Boolean(tipo.fixed?.parcial));
              setRetribuida(tipo.fixed?.retribuida ?? true);
              setMsg("");
              setCheck(null);
              setValError("");
            }}
            className="px-4 py-2 rounded-lg bg-white/70 hover:bg-white border border-white/60 shadow-sm"
          >
            Limpiar
          </button>
          <span
            className={`text-sm min-h-[1.5rem] ${
              (msg && msg.startsWith("✅")) || (!msg && !valError) ? "text-emerald-700" : "text-rose-700"
            }`}
            role="status"
            aria-live="polite"
          >
            {checking ? "Validando…" : msg || (valError ? `❌ ${valError}` : "")}
          </span>
        </div>
      </div>

      {/* derecha: resumen */}
      <aside className="rounded-2xl bg-white/55 backdrop-blur-xl border border-white/50 shadow p-4 sm:p-5 space-y-4">
        <h3 className="text-base sm:text-lg font-semibold text-slate-900">Resumen</h3>

        <div
          className={`p-3 rounded-xl ring-1 ${TIPO_THEME[tipo.modelTipo]?.ring || "ring-slate-300/40"} ${
            TIPO_THEME[tipo.modelTipo]?.bg || "bg-slate-500/10"
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <span className={`h-2.5 w-2.5 rounded-full ${TIPO_THEME[tipo.modelTipo]?.dot || "bg-slate-400"}`} />
            <span className={`text-sm font-medium ${TIPO_THEME[tipo.modelTipo]?.text || "text-slate-800"}`}>
              {tipo.label} · {subtipo?.label || "—"}
            </span>
          </div>
          <div className="text-sm text-slate-700">
            <div>
              <strong>Desde:</strong> {fmtFechaES(fechaInicio)}{" "}
              {(parcial || esSubtipoPorHoras(subtipo)) && horaInicio ? `(${fmtH(horaInicio)})` : ""}
            </div>
            <div>
              <strong>Hasta:</strong> {fmtFechaES(fechaFin)}{" "}
              {(parcial || esSubtipoPorHoras(subtipo)) && horaFin ? `(${fmtH(horaFin)})` : ""}
            </div>
            {!parcial && !esSubtipoPorHoras(subtipo) ? (
              <div className="mt-1">
                <strong>Días solicitados:</strong>{" "}
                {checking ? "…" : check?.requested_days ?? diasLaborablesFallback(fechaInicio, fechaFin)}
                {check && Number.isFinite(check.available_days) && (
                  <>
                    {" "}
                    · <strong>Disponibles:</strong> {check.available_days}
                  </>
                )}
              </div>
            ) : (
              <div className="mt-1">
                <strong>Duración parcial:</strong> {Math.floor((horasParciales || 0) / 60)}h {(horasParciales || 0) % 60}m
              </div>
            )}
            <div className="mt-1">
              <strong>Cómputo:</strong>{" "}
              {check?.computo?.toLowerCase?.() || reglaTipo?.computo?.toLowerCase?.() || "laborables"} ·{" "}
              <strong>Mediodía:</strong> {permiteMedio ? "sí" : "no"}
            </div>
            {check && !check.allowed && (
              <div className="mt-1 text-rose-700">
                {check.reason === "saldo_insuficiente"
                  ? `No hay saldo suficiente. Pides ${check.requested_days}, disponibles ${check.available_days}.`
                  : check.reason === "medio_dia_no_permitido"
                  ? "El convenio no permite medio día para este tipo."
                  : "Solicitud no permitida."}
              </div>
            )}
          </div>
        </div>

        {tipo.modelTipo === "VACACIONES" && (
          <div className="text-xs text-slate-600">
            Las vacaciones son de día completo y retribuidas. No se permiten parciales.
          </div>
        )}
      </aside>
    </form>
  );
}
